import React, { useState, useEffect, useCallback } from 'react';
import universalDataService from '../services/universalDataService';
import { getEqCostBreakdown } from './EquipmentManager';

const CAT_COLORS = {
  'Semănat': '#166534', 'Fertilizare': '#1e40af', 'Erbicidare': '#7c3aed',
  'Tratament': '#be123c', 'Arat': '#92400e', 'Discuit': '#78350f',
  'Recoltat': '#d97706', 'Irigat': '#0891b2', 'Altele': '#4b5563',
  '⛽ Motorină': '#d97706'
};

const perHa = (cost, ha) => ha > 0 ? (cost / ha).toFixed(0) : '—';

// Uses getEqCostBreakdown so valoareRevanzare is always accounted for
const calcEquipmentAnnualCost = (utilaje, year) => {
  let total = 0;
  utilaje.forEach(eq => {
    const { costAnual, aniPastrare } = getEqCostBreakdown(eq);
    const start = eq.acquisitionType === 'cash' ? (eq.purchaseYear || year) : (eq.leasingStartYear || year);
    if (year >= start && year <= start + aniPastrare - 1) total += costAnual;
    (eq.repairs || []).forEach(r => {
      const ry = parseInt((r.date || '').split('-')[0]) || year;
      if (r.amortizationType === 'current') {
        if (ry === year) total += (r.cost || 0);
      } else {
        const ryrs = r.amortizationYears || 1;
        if (year >= ry && year < ry + ryrs) total += (r.cost || 0) / ryrs;
      }
    });
  });
  return total;
};

const Costuri = () => {
  const [lucrari, setLucrari] = useState([]);
  const [fields, setFields] = useState([]);
  const [utilaje, setUtilaje] = useState([]);
  const [motorina, setMotorina] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState('parcel');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [lData, fData, uData, mData] = await Promise.all([
        universalDataService.loadLucrari(),
        universalDataService.loadFields().catch(() => []),
        universalDataService.loadUtilaje(),
        universalDataService.loadMotorina()
      ]);
      setLucrari(Array.isArray(lData) ? lData : []);
      setFields(Array.isArray(fData) ? fData : []);
      setUtilaje(Array.isArray(uData) ? uData : []);
      setMotorina(mData || null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Key areas ─────────────────────────────────────────────────────────────
  // totalArableHa: ALL arable fields — denominator for equipment/fixed costs
  const totalArableHa = fields
    .filter(f => !f.folosinta || f.folosinta === 'arabil')
    .reduce((s, f) => s + (parseFloat(f.area) || 0), 0);

  // Average motorina price
  const avgMotorinaPrice = (() => {
    if (!motorina?.purchases?.length) return 0;
    const liters = motorina.purchases.reduce((s, p) => s + (p.liters || 0), 0);
    const cost = motorina.purchases.reduce((s, p) => s + ((p.liters || 0) * (p.pricePerLiter || 0)), 0);
    return liters > 0 ? cost / liters : 0;
  })();

  const yearLucrari = lucrari.filter(l => l.year === selectedYear);
  const availableYears = [...new Set(lucrari.map(l => l.year))].sort((a, b) => b - a);

  // totalWorkedHa: ha actually covered by lucrari this year (for material costs)
  const totalWorkedHa = (() => {
    const ids = new Set(yearLucrari.flatMap(l => (l.parcele || []).map(p => p.fieldId)));
    return [...ids].reduce((s, fid) => s + (parseFloat(fields.find(f => f.id === fid)?.area) || 0), 0);
  })();

  // Motorina cost this year from dispensing log
  const motorinaCostYear = (() => {
    if (!motorina?.dispensing) return 0;
    const liters = motorina.dispensing
      .filter(d => d.toType === 'utilaj' && new Date(d.date).getFullYear() === selectedYear)
      .reduce((s, d) => s + (d.liters || 0), 0);
    return liters * avgMotorinaPrice;
  })();

  // Equipment annual cost
  const eqCostYear = calcEquipmentAnnualCost(utilaje, selectedYear);
  const matCostYear = yearLucrari.reduce((s, l) => s + (l.materiale || []).reduce((ms, m) => ms + (m.totalCost || 0), 0), 0);

  // ── Per-parcel report ─────────────────────────────────────────────────────
  const buildParcelReport = () => {
    const fieldMap = {};
    fields.forEach(f => { fieldMap[f.id] = f; });
    const allIds = new Set(yearLucrari.flatMap(l => (l.parcele || []).map(p => p.fieldId)));
    return [...allIds].map(fieldId => {
      const field = fieldMap[fieldId];
      const fieldHa = parseFloat(field?.area) || 0;
      let matCost = 0, motCost = 0;
      const byType = {};
      yearLucrari.forEach(l => {
        const parcela = (l.parcele || []).find(p => p.fieldId === fieldId);
        if (!parcela) return;
        const ratio = l.totalHa > 0 ? parcela.ha / l.totalHa : 0;
        (l.materiale || []).forEach(m => {
          const c = (m.totalCost || 0) * ratio;
          matCost += c;
          byType[l.type || 'Altele'] = (byType[l.type || 'Altele'] || 0) + c;
        });
        if (l.motorina?.totalLiters > 0) {
          const c = (l.motorina.totalLiters || 0) * ratio * (l.motorina.pricePerLiter || avgMotorinaPrice);
          motCost += c;
          byType['⛽ Motorină'] = (byType['⛽ Motorină'] || 0) + c;
        }
      });
      const total = matCost + motCost;
      return {
        fieldId, fieldName: field?.name || `Câmp ${fieldId}`, fieldHa,
        matCost, motCost, total, costPerHa: fieldHa > 0 ? total / fieldHa : 0, byType
      };
    }).sort((a, b) => b.total - a.total);
  };

  // ── Per-operation report ──────────────────────────────────────────────────
  const buildOperationReport = () => {
    const map = {};
    yearLucrari.forEach(l => {
      const t = l.type || 'Altele';
      if (!map[t]) map[t] = { type: t, count: 0, totalHa: 0, matCost: 0, motCost: 0 };
      map[t].count++;
      map[t].totalHa += l.totalHa || 0;
      map[t].matCost += (l.materiale || []).reduce((s, m) => s + (m.totalCost || 0), 0);
      if (l.motorina?.totalLiters > 0)
        map[t].motCost += (l.motorina.totalLiters || 0) * (l.motorina.pricePerLiter || avgMotorinaPrice);
    });
    return Object.values(map).map(r => ({ ...r, total: r.matCost + r.motCost }))
      .sort((a, b) => b.total - a.total);
  };

  // ── Per-equipment report ──────────────────────────────────────────────────
  const buildEquipmentReport = () => {
    return utilaje.map(eq => {
      const { costAnual, aniPastrare, valBlocataAnual } = getEqCostBreakdown(eq);
      const start = eq.acquisitionType === 'cash' ? (eq.purchaseYear || selectedYear) : (eq.leasingStartYear || selectedYear);
      const inRange = selectedYear >= start && selectedYear <= start + aniPastrare - 1;
      const acqCost = inRange ? costAnual : 0;
      const valBlocatAnual = inRange ? valBlocataAnual : 0;
      let repCost = 0;
      (eq.repairs || []).forEach(r => {
        const ry = parseInt((r.date || '').split('-')[0]) || selectedYear;
        if (r.amortizationType === 'current') { if (ry === selectedYear) repCost += r.cost || 0; }
        else {
          const ryrs = r.amortizationYears || 1;
          if (selectedYear >= ry && selectedYear < ry + ryrs) repCost += (r.cost || 0) / ryrs;
        }
      });
      return { ...eq, acqCost, repCost, total: acqCost + repCost, valBlocatAnual };
    }).filter(e => e.total > 0 || e.valBlocatAnual > 0);
  };

  if (loading) return <div style={S.page}><div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>Se încarcă…</div></div>;

  const parcelReport = viewMode === 'parcel' ? buildParcelReport() : [];
  const opReport = viewMode === 'operatie' ? buildOperationReport() : [];
  const eqReport = viewMode === 'echipamente' ? buildEquipmentReport() : [];
  const totalAllCosts = matCostYear + motorinaCostYear + eqCostYear;

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>📊 Raport Costuri {selectedYear}</h2>
          <span style={S.sub}>
            {totalArableHa > 0 && <><strong>{totalArableHa.toFixed(1)} ha arabile</strong> totale · </>}
            {totalWorkedHa > 0 && <>{totalWorkedHa.toFixed(1)} ha lucrate</>}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedYear} onChange={e => setSelectedYear(+e.target.value)} style={S.select}>
            {(availableYears.length ? availableYears : [new Date().getFullYear()]).map(y =>
              <option key={y} value={y}>{y}</option>
            )}
          </select>
          <button onClick={loadData} style={S.refreshBtn}>🔄</button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={S.summaryRow}>
        {/* Materiale */}
        <div style={{ ...S.summCard, borderColor: '#1e40af' }}>
          <div style={S.summIcon}>📦</div>
          <div style={S.summLabel}>Materiale</div>
          <div style={{ ...S.summValue, color: '#1e40af' }}>{matCostYear.toFixed(0)} lei</div>
          <div style={S.summHaLine}>
            {totalWorkedHa > 0
              ? <><span style={S.haVal}>{perHa(matCostYear, totalWorkedHa)}</span><span style={S.haUnit}> lei/ha lucrată</span></>
              : <span style={S.haDim}>— ha lucrate</span>}
          </div>
        </div>

        {/* Motorina */}
        <div style={{ ...S.summCard, borderColor: '#d97706' }}>
          <div style={S.summIcon}>⛽</div>
          <div style={S.summLabel}>Motorină</div>
          <div style={{ ...S.summValue, color: '#d97706' }}>{motorinaCostYear.toFixed(0)} lei</div>
          <div style={S.summHaLine}>
            {totalArableHa > 0
              ? <><span style={S.haVal}>{perHa(motorinaCostYear, totalArableHa)}</span><span style={S.haUnit}> lei/ha arabilă</span></>
              : <span style={S.haDim}>— ha arabile</span>}
          </div>
        </div>

        {/* Utilaje */}
        <div style={{ ...S.summCard, borderColor: '#4b5563' }}>
          <div style={S.summIcon}>🚜</div>
          <div style={S.summLabel}>Utilaje & Amortizare</div>
          <div style={{ ...S.summValue, color: '#4b5563' }}>{eqCostYear.toFixed(0)} lei</div>
          <div style={S.summHaLine}>
            {totalArableHa > 0
              ? <><span style={{ ...S.haVal, color: '#4b5563' }}>{perHa(eqCostYear, totalArableHa)}</span><span style={S.haUnit}> lei/ha arabilă</span></>
              : <span style={S.haDim}>— ha arabile</span>}
          </div>
          {totalArableHa > 0 && <div style={S.haDim}>÷ {totalArableHa.toFixed(1)} ha arabile</div>}
        </div>

        {/* Total */}
        <div style={{ ...S.summCard, borderColor: '#dc2626', background: '#fff8f8' }}>
          <div style={S.summIcon}>💰</div>
          <div style={S.summLabel}>Total Costuri</div>
          <div style={{ ...S.summValue, color: '#dc2626', fontWeight: 800, fontSize: 24 }}>{totalAllCosts.toFixed(0)} lei</div>
          <div style={S.summHaLine}>
            {totalArableHa > 0
              ? <><span style={{ ...S.haVal, color: '#dc2626', fontSize: 15 }}>{perHa(totalAllCosts, totalArableHa)}</span><span style={S.haUnit}> lei/ha arabilă</span></>
              : <span style={S.haDim}>— ha arabile</span>}
          </div>
          {totalWorkedHa > 0 && totalArableHa !== totalWorkedHa && (
            <div style={S.haDim}>{perHa(totalAllCosts, totalWorkedHa)} lei/ha lucrată</div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {[['parcel','🌾 Pe Câmp'], ['operatie','🚜 Pe Operație'], ['echipamente','⚙️ Utilaje & Amortizare']].map(([id, lbl]) => (
          <button key={id} onClick={() => setViewMode(id)}
            style={{ ...S.tab, ...(viewMode === id ? S.tabActive : {}) }}>{lbl}</button>
        ))}
      </div>

      {/* ── PER CÂMP ──────────────────────────────────────────────────────── */}
      {viewMode === 'parcel' && (
        <div>
          {yearLucrari.length === 0
            ? <div style={S.empty}><div style={{ fontSize: 36, marginBottom: 8 }}>🌾</div><div>Nicio lucrare în {selectedYear}.</div><div style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>Adăugați lucrări în secțiunea Lucrări.</div></div>
            : parcelReport.length === 0
              ? <div style={S.empty}>Nicio lucrare pe câmpuri în {selectedYear}.</div>
              : parcelReport.map(r => (
                <div key={r.fieldId} style={S.parcelCard}>
                  <div style={S.parcelHeader}>
                    <div>
                      <span style={S.parcelName}>{r.fieldName}</span>
                      <span style={S.parcelHa}>{r.fieldHa.toFixed(2)} ha</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={S.bigCost}>{r.total.toFixed(0)} lei</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#dc2626' }}>
                        {r.fieldHa > 0 ? <>{(r.total / r.fieldHa).toFixed(0)} <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>lei/ha</span></> : '—'}
                      </div>
                    </div>
                  </div>
                  <div style={S.byTypeRow}>
                    {Object.entries(r.byType).sort((a, b) => b[1] - a[1]).map(([type, cost]) => (
                      <div key={type} style={S.typeChip}>
                        <span style={{ ...S.typeDot, background: CAT_COLORS[type] || '#6b7280' }} />
                        <span style={{ fontSize: 12 }}>{type}</span>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{cost.toFixed(0)} lei</span>
                        {r.fieldHa > 0 && <span style={{ fontSize: 11, color: '#9ca3af' }}>{(cost / r.fieldHa).toFixed(0)}/ha</span>}
                      </div>
                    ))}
                  </div>
                  {/* Stacked bar */}
                  <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', gap: 1 }}>
                    {Object.entries(r.byType).map(([type, cost]) => {
                      const pct = r.total > 0 ? (cost / r.total) * 100 : 0;
                      return <div key={type} title={`${type}: ${cost.toFixed(0)} lei`}
                        style={{ height: 8, width: `${pct}%`, background: CAT_COLORS[type] || '#6b7280', minWidth: pct > 2 ? 2 : 0 }} />;
                    })}
                  </div>
                </div>
              ))
          }
        </div>
      )}

      {/* ── PER OPERAȚIE ──────────────────────────────────────────────────── */}
      {viewMode === 'operatie' && (
        <div>
          {yearLucrari.length === 0
            ? <div style={S.empty}><div style={{ fontSize: 36, marginBottom: 8 }}>🚜</div><div>Nicio lucrare în {selectedYear}.</div></div>
            : <div style={S.tableWrap}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={S.thead}>
                    {['Operație', 'Nr.', 'Ha', 'Materiale (lei)', 'Motorină (lei)', 'Total (lei)', 'Lei/ha'].map(h =>
                      <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {opReport.map(r => (
                      <tr key={r.type} style={S.trow}>
                        <td style={S.td}>
                          <span style={{ ...S.typeDot, background: CAT_COLORS[r.type] || '#4b5563', display: 'inline-block', marginRight: 6 }} />
                          {r.type}
                        </td>
                        <td style={{ ...S.td, textAlign: 'center' }}>{r.count}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{r.totalHa.toFixed(1)}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{r.matCost.toFixed(0)}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{r.motCost.toFixed(0)}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{r.total.toFixed(0)}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>
                          {r.totalHa > 0 ? (r.total / r.totalHa).toFixed(0) : '—'}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: '#fef2f2', fontWeight: 700 }}>
                      <td style={S.td} colSpan={2}>TOTAL</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{totalWorkedHa.toFixed(1)}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{matCostYear.toFixed(0)}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{motorinaCostYear.toFixed(0)}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#dc2626' }}>{(matCostYear + motorinaCostYear).toFixed(0)}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#dc2626' }}>
                        {totalWorkedHa > 0 ? ((matCostYear + motorinaCostYear) / totalWorkedHa).toFixed(0) : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
          }
        </div>
      )}

      {/* ── UTILAJE & AMORTIZARE ──────────────────────────────────────────── */}
      {viewMode === 'echipamente' && (
        <div>
          {eqReport.length === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>⚙️</div>
              <div>Niciun utilaj cu costuri de amortizare în {selectedYear}.</div>
              <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>Adăugați utilaje în Manager Echipamente.</div>
            </div>
          ) : (
            <>
              <div style={S.tableWrap}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={S.thead}>
                    {['Utilaj', 'Tip', `Cost net/an (lei)`, `Reparații (lei)`, 'Total (lei)', 'Lei/ha arabilă', 'Blocat în utilaj/an'].map(h =>
                      <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {eqReport.map(eq => (
                      <tr key={eq.id} style={S.trow}>
                        <td style={S.td}><strong>{eq.name}</strong>{eq.brand ? <><br/><span style={{ fontSize: 11, color: '#9ca3af' }}>{eq.brand}</span></> : null}</td>
                        <td style={{ ...S.td, textAlign: 'center' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: eq.acquisitionType === 'leasing' ? '#ede9fe' : '#dcfce7', color: eq.acquisitionType === 'leasing' ? '#6d28d9' : '#166534' }}>
                            {eq.acquisitionType === 'leasing' ? 'Leasing' : 'Cash'}
                          </span>
                        </td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{eq.acqCost.toFixed(0)}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{eq.repCost.toFixed(0)}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{eq.total.toFixed(0)}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>
                          {totalArableHa > 0 ? (eq.total / totalArableHa).toFixed(0) : '—'}
                        </td>
                        <td style={{ ...S.td, textAlign: 'right', color: '#9ca3af', fontSize: 12 }}>
                          {eq.valBlocatAnual > 0 ? `${eq.valBlocatAnual.toFixed(0)} lei` : '—'}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: '#fef2f2', fontWeight: 700 }}>
                      <td style={S.td} colSpan={2}>TOTAL UTILAJE</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{eqReport.reduce((s, e) => s + e.acqCost, 0).toFixed(0)}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{eqReport.reduce((s, e) => s + e.repCost, 0).toFixed(0)}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#dc2626' }}>{eqCostYear.toFixed(0)}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#dc2626' }}>
                        {totalArableHa > 0 ? (eqCostYear / totalArableHa).toFixed(0) : '—'}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#9ca3af', fontSize: 12 }}>
                        {eqReport.reduce((s, e) => s + (e.valBlocatAnual || 0), 0) > 0
                          ? `${eqReport.reduce((s, e) => s + (e.valBlocatAnual || 0), 0).toFixed(0)} lei`
                          : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
                {(() => {
                  const totalValBlocata = eqReport.reduce((s, e) => s + (e.valBlocatAnual || 0), 0);
                  return (
                    <div style={{ padding: '12px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 13, color: '#374151', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div>
                        Costul net de amortizare se împarte la totalul suprafeței arabile ({totalArableHa.toFixed(1)} ha)
                        {totalArableHa === 0 && <span style={{ color: '#dc2626' }}> — adăugați câmpuri arabile în secțiunea Câmpuri.</span>}
                      </div>
                      {totalValBlocata > 0 && (
                        <div style={{ color: '#6b7280', borderTop: '1px solid #e5e7eb', paddingTop: 6, marginTop: 2 }}>
                          ⚠️ <strong>{totalValBlocata.toFixed(0)} lei/an</strong> este valoare recuperabilă la vânzarea utilajelor —
                          inclusă în costul total dar <strong>blocată în active, nu disponibilă ca numerar</strong>.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const S = {
  page: { padding: 24, maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: '#1f2937' },
  sub: { color: '#6b7280', fontSize: 13 },
  select: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, background: '#fff' },
  refreshBtn: { padding: '8px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 24 },
  summCard: { background: '#fff', border: '2px solid', borderRadius: 10, padding: '16px', textAlign: 'center' },
  summIcon: { fontSize: 24, marginBottom: 4 },
  summLabel: { fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 },
  summValue: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  summHaLine: { marginBottom: 2 },
  haVal: { fontSize: 18, fontWeight: 800, color: '#dc2626' },
  haUnit: { fontSize: 11, color: '#6b7280' },
  haDim: { fontSize: 11, color: '#9ca3af', marginTop: 2 },
  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb' },
  tab: { padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#6b7280', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { color: '#166534', borderBottomColor: '#166534', fontWeight: 700, background: '#f0fdf4', borderRadius: '4px 4px 0 0' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#6b7280' },
  parcelCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px', marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  parcelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 },
  parcelName: { fontSize: 17, fontWeight: 700, color: '#1f2937' },
  parcelHa: { fontSize: 12, color: '#6b7280', marginLeft: 8, background: '#f3f4f6', padding: '2px 8px', borderRadius: 10 },
  bigCost: { fontSize: 20, fontWeight: 800, color: '#1f2937' },
  byTypeRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  typeChip: { display: 'flex', gap: 5, alignItems: 'center', background: '#f9fafb', border: '1px solid #e5e7eb', padding: '4px 10px', borderRadius: 20 },
  typeDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  tableWrap: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' },
  thead: { background: '#f9fafb' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e5e7eb', textTransform: 'uppercase' },
  trow: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 12px', fontSize: 14, color: '#374151' },
};

export default Costuri;
